/**
 * butterToast — the unified toast API for every Butter client app.
 *
 * Two tiers:
 *
 * 1. `butterToast(response, title, overrides?)` — the primary, API-driven path.
 *    The response is the typed source of truth: its `success` picks the variant
 *    and its `message` becomes the body. Pass `title` for an intentional label,
 *    or `null` to fall back to a generic "Success" / "Error". `overrides` can
 *    flip `success`, replace `message`, or tweak `behavior` for this one toast.
 *
 * 2. `butterToast.info / .success / .error(message, title?, behavior?)` — take a
 *    message directly (a string, an `Error`, or any caught value), for when you
 *    don't have a response envelope: client validation, a `catch (e)` block, or a
 *    service that throws. Prefer the response form above whenever you do have an
 *    envelope. `info` has no envelope equivalent (advisory only).
 *
 * All visuals live in {@link ToastView}; this file is logic only.
 */
import { toast as sonner } from 'sonner'
import type { ExternalToast } from 'sonner'

import { ToastView } from './ToastView'
import { CountdownToastView } from './CountdownToastView'
import type {
  ToastableResponse,
  ToastBehavior,
  ToastId,
  ToastOverrides,
  ToastVariant,
} from './types'

const GENERIC_TITLE: Record<ToastVariant, string> = {
  success: 'Success',
  error: 'Error',
  info: 'Info',
}

/** Used when a non-string / messageless value is passed to a helper. */
const FALLBACK_MESSAGE: Record<ToastVariant, string> = {
  success: '',
  error: 'Something went wrong. Please try again.',
  info: '',
}

/**
 * Resolve a helper's message argument to display text. Accepts a plain string,
 * an `Error` (uses its `message`), or any other caught value — `catch (e)` is
 * `unknown` under strict TS, so this lets `butterToast.error(e, title)` work
 * with no `instanceof` dance. Falls back to the variant default when there's
 * nothing usable.
 */
function resolveMessage(input: unknown, variant: ToastVariant): string {
  if (typeof input === 'string') return input
  if (input instanceof Error && input.message) return input.message
  return FALLBACK_MESSAGE[variant]
}

/** Render a resolved toast through sonner's custom renderer. */
function emit(args: {
  variant: ToastVariant
  title: string
  message?: string
  behavior?: ToastBehavior
}): ToastId {
  const { variant, title, message, behavior } = args

  // Only forward keys that are actually set. sonner's `custom` spreads this
  // object on top of its generated `{ id }`, so passing `id: undefined` here
  // clobbers the real id and breaks per-toast dismissal.
  const options: ExternalToast = {}
  if (behavior?.id !== undefined) options.id = behavior.id
  if (behavior?.duration !== undefined) options.duration = behavior.duration
  if (behavior?.position !== undefined) options.position = behavior.position

  return sonner.custom(
    (id) => (
      <ToastView
        id={id}
        variant={variant}
        title={title}
        message={message}
        dismissible={behavior?.dismissible ?? true}
        action={behavior?.action}
      />
    ),
    options,
  )
}

/** Primary API-driven entry point. */
function fromResponse(
  response: ToastableResponse,
  title: string | null,
  overrides: ToastOverrides = {},
): ToastId {
  const success = overrides.success ?? response.success
  const variant: ToastVariant = success ? 'success' : 'error'
  return emit({
    variant,
    title: title ?? GENERIC_TITLE[variant],
    message: overrides.message ?? response.message,
    behavior: overrides.behavior,
  })
}

/** Build a message helper for a fixed variant. */
function clientToast(variant: ToastVariant) {
  return (
    // `unknown` so `catch (e)` values pass directly; resolveMessage coerces.
    message: unknown,
    title: string | null = null,
    behavior?: ToastBehavior,
  ): ToastId =>
    emit({
      variant,
      title: title ?? GENERIC_TITLE[variant],
      message: resolveMessage(message, variant),
      behavior,
    })
}

export const butterToast = Object.assign(fromResponse, {
  /** Advisory toast from a message — no envelope equivalent. */
  info: clientToast('info'),
  /** Success toast from a message (use the response form when you have an envelope). */
  success: clientToast('success'),
  /** Error toast from a message, `Error`, or caught value — e.g. `catch (e) { butterToast.error(e, 'Save failed') }`. Use the response form when you have an envelope. */
  error: clientToast('error'),
  /** Dismiss a toast by id, or all toasts when called with no argument. */
  dismiss: (id?: ToastId): void => {
    sonner.dismiss(id)
  },
  /**
   * A countdown-action toast: shows a title/message with an optional draining progress
   * bar. When `seconds` is set the bar fires `onComplete` at zero (idle self-commit); the
   * action button fires it immediately; cancel dismisses. Committing closes the toast
   * too, so `onComplete` does not have to navigate away to get rid of it. Pass a stable
   * `id` to dedupe repeat toasts. Used for "New Version Available" auto-refresh, but
   * generic by design.
   */
  countdown: (opts: {
    title: string
    message?: string
    seconds?: number
    actionLabel?: string
    cancelLabel?: string
    variant?: ToastVariant
    onComplete: () => void
    /** Stable key — dedupes/updates in place. Omit to let sonner generate one. */
    id?: ToastId
  }): ToastId => {
    // Never forward `id: undefined` — sonner spreads options over its generated id, so an
    // undefined id would clobber it (same guard as `emit`).
    const options: ExternalToast = { duration: Infinity } // the countdown owns closing
    if (opts.id !== undefined) options.id = opts.id
    return sonner.custom(
      (toastId) => (
        <CountdownToastView
          id={toastId}
          title={opts.title}
          message={opts.message}
          seconds={opts.seconds}
          actionLabel={opts.actionLabel}
          cancelLabel={opts.cancelLabel}
          variant={opts.variant}
          onComplete={opts.onComplete}
        />
      ),
      options,
    )
  },
})

export type ButterToast = typeof butterToast
